import { useId, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, MessageSquare } from 'lucide-react';
import { assembleTextSendMessage } from '@signalvote/shared';
import { PhonePreviewSheet, type TextSendContent } from '@/components/work-orders';

/**
 * On phones, where the preview can't sit beside the fields: the text as it lands on a lock screen, live as
 * the person types. One tap opens the full preview.
 */
export function PreviewNotification(content: TextSendContent) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const text = content.body.trim() ? assembleTextSendMessage(content.body, content.paidForDisclaimer) : 'Your message appears here…';
  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="mb-6 md:hidden"
    >
      <button
        type="button"
        aria-label="Preview what voters see"
        aria-describedby={`${id}-text`}
        onClick={() => setOpen(true)}
        className="w-full rounded-2xl bg-foreground p-3.5 text-left text-background shadow-lg transition-transform duration-[160ms] ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <span className="flex items-center gap-2 text-xs">
          <span className="grid h-5 w-5 place-items-center rounded-md bg-primary text-primary-foreground">
            <MessageSquare className="h-3 w-3" aria-hidden="true" />
          </span>
          <span className="font-semibold">Text message</span>
          <span className="ml-auto opacity-60">now</span>
        </span>
        <span id={`${id}-text`} className="mt-1.5 line-clamp-2 text-[0.9375rem] leading-snug [overflow-wrap:anywhere]">
          {text}
        </span>
        <span className="mt-2 flex items-center gap-1 text-xs font-medium opacity-70">
          Preview what voters see
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      </button>
      <PhonePreviewSheet {...content} open={open} onOpenChange={setOpen} />
    </motion.div>
  );
}
